import React, { useEffect, useState, useCallback, useMemo } from 'react';
import classnames from 'classnames';
import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client';
import _ from 'lodash';

import makeStyles from '@mui/styles/makeStyles';
import Table from '@mui/material/Table';
import TableRow from '@mui/material/TableRow';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import { Box, CircularProgress, IconButton, TableHead, TextField, Button } from '@mui/material';

import Snackbar from '@mui/material/Snackbar';
import { Alert } from '@mui/material';

import { Tab, Tabs } from '@mui/material';
import TabPanel from '../../../components/TabPanel';

// core components
import styles from 'assets/jss/material-dashboard-pro-react/components/tasksStyle.js';
import iconPlusSvg from '../../../assets/img/icons/plus.svg';
import iconMinusSvg from '../../../assets/img/icons/minus.svg';
import { theme } from '../../../theme';
import { legalStructureOption } from '../../../utils';
import { Clear } from '@mui/icons-material';
import twoWayArrow from '../../../assets/img/icons/two-way-arrow.png';
import TextFieldComp from './FieldComps/TextField';
import { DateFieldComp } from './FieldComps/DateField';
import SelectFieldComp from './FieldComps/SelectField';
import { getCountriesArray, getDaysArray, getMonthsArray, getStatesArray, getYearsArray } from '../../../utils';

import moment from 'moment';

// Fix for MUI v5 type error: cast styles as any and fix lineBreak property
const fixedStyles = { ...styles } as any;
if (fixedStyles.tooltip && typeof fixedStyles.tooltip.lineBreak === 'string') {
  // MUI expects lineBreak to be 'auto' | 'normal' | 'unset' | undefined
  if (!['auto', 'normal', 'unset', undefined].includes(fixedStyles.tooltip.lineBreak)) {
    fixedStyles.tooltip.lineBreak = 'auto';
  }
}

// Add types for the main data structures (partial, for clarity)
export interface LenderFieldDetails {
  displayName: string;
  section: string;
  category: string;
  dcrPropertySource: string;
  fieldInputType: string;
  required: string;
  notesInternal: string;
  notesExternal: string;
  hidden: boolean;
  readOnly: boolean;
  rulesConfiguration: any;
  ordinal: number;
  lenderFieldParentId: string;
  lenderFieldParentLenderProperty: string;
  validRegexPattern: string;
  inputErrorHelperText: string;
  optionSets: any[];
  errors?: { name: string; message: string }[];
}

export interface DynamicLenderMappingField {
  lenderFieldId: string;
  lenderProperty: string;
  mappedValue: any;
  originalMappedValue: any;
  details: LenderFieldDetails;
}

interface LMSMappingModalProps {
  vo: any;
  lender: any;
  errors: string[];
  lmsCodeName: string;
  lmsName: string;

  submitCallback: (mapping: DynamicLenderMappingField[]) => void;
  handleClose: () => void;

  submissionMessage: string;
  portalConfiguration: any;
  transactionSummary: any;
}

const useStyles = makeStyles((theme: any) => ({
  ...fixedStyles,
  ...styles,
  modalStyle: {
    position: 'absolute',
    backgroundColor: theme.palette.background.paper,
    border: '2px solid #000',
    boxShadow: theme.shadows[5],
    padding: theme.spacing(2, 4, 3),
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    minWidth: 1200,
    '@media (max-width: 1200px)': {
      width: '120vw',
      minWidth: '120vw',
    },
  },
  dateSelect: {
    marginTop: '11px',
    width: '100%',
    '& label': {
      color: '#000000',
      fontSize: 14,
      fontWeight: 400,
    },
  },
  inputLabel: {
    transform: 'translate(14px, 12px) scale(1)',
  },
  labelNotMapped: {
    width: '100%',
    float: 'right',
    textAlign: 'right',
  },
  textField: {
    margin: '8px 0 0',
    width: '100%',
    height: 40,
    '& input': {
      padding: 12,
      fontSize: 14,
    },
    '& fieldset:disabled': {
      borderColor: '#f44336',
    },
    '& .MuiOutlinedInput-input': {
      padding: '12px 24px',
    },
    '& fieldset legend span': {
      fontSize: 12,
      fontFamily: `"Roboto", "Helvetica", "Arial", sans-serif`,
      textTransform: 'none',
      fontWeight: 'normal',
    },
  },
  wrapperLoader: {
    display: 'flex',
    flexDirection: 'column',
    color: theme.palette.grey[500],
    width: '100%',
    alignItems: 'center',
    margin: '64px 0',
    '& .headerContainer': {
      display: 'flex',
      alignItems: 'center',
      marginBottom: 32,
      '& h4': {
        marginLeft: 8,
      },
    },
    '& .progressLoader': {
      margin: 32,
      color: theme.palette.grey[500],
    },
  },
  selectStyles: {
    height: 40,
    '& .MuiOutlinedInput-input': {
      padding: '8px 24px',
    },
    '& fieldset legend span': {
      padding: 0,
      fontSize: '0.85em',
    },
  },
  selectLabel: {
    transform: 'translate(14px, 12px) scale(1)',
    display: '-webkit-box',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    '-webkit-line-clamp': 1,
    '-webkit-box-orient': 'vertical',
  },
  collapseButton: {
    top: 0,
    right: 0,
    width: 50,
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    fontSize: 20,
    zIndex: 3,
    cursor: 'pointer',
    color: '#999',
  },
  icon: {
    width: 20,
    height: 20,
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'contain',
    backgroundPosition: 'center',
    display: 'inline-block',
    opacity: 0.3,
  },
  iconPlus: {
    backgroundImage: `url("${iconPlusSvg}")`,
  },
  iconMinus: {
    backgroundImage: `url("${iconMinusSvg}")`,
  },
}));

const GET_DYNAMIC_LENDER_MAPPING = gql`
  query MappedLenderValuesForVendorOpportunity(
    $lenderType: LenderType!
    $vendorOpportunityId: ID!
    $lenderProfileDynamicsId: ID!
  ) {
    getMappedLenderValuesForVendorOpportunity(
      lenderType: $lenderType
      vendorOpportunityId: $vendorOpportunityId
      lenderProfileDynamicsId: $lenderProfileDynamicsId
    ) {
      lenderFieldId
      lenderProperty
      mappedValue
      originalMappedValue
      details {
        displayName
        section
        category
        dcrPropertySource
        fieldInputType
        required
        notesInternal
        notesExternal
        hidden
        readOnly
        rulesConfiguration
        ordinal
        lenderFieldParentId
        lenderFieldParentLenderProperty
        validRegexPattern
        inputErrorHelperText
        optionSets {
          displayName
          lenderFieldId
          lenderFieldOptionSetId
          lenderFieldOptionSetParentId
          lenderValue
        }
      }
    }
  }
`;

const LMSMappingModal: React.FC<LMSMappingModalProps> = ({
  vo,
  lender,
  errors,
  lmsCodeName,
  lmsName,
  submitCallback,
  handleClose,
  submissionMessage,
  portalConfiguration,
  transactionSummary,
}) => {
  function structureFields(fields: any[], identifier: string): any {
    const obj: any = {};
    fields.forEach((field: any) => {
      const key = field.details?.[identifier];
      if (!key) {
        obj['DEFAULT'] = obj['DEFAULT'] ? [...obj['DEFAULT'], field] : [field];
      } else {
        obj[key] = obj[key] ? [...obj[key], field] : [field];
      }
    });
    return obj;
  }

  function sortByOrdinal(mapping: any): any {
    const sortedMapping: any = {};
    Object.entries(mapping)
      .sort((a: any, b: any) => {
        let A = null;
        let B = null;
        try {
          A = a[1].DEFAULT[0].details?.ordinal;
        } catch (e) {
          A = null;
        }
        try {
          B = b[1].DEFAULT[0].details?.ordinal;
        } catch (e) {
          B = null;
        }
        if (A === null) return 1;
        if (B === null) return -1;
        return A < B ? -1 : 1;
      })
      .forEach((entry: any) => {
        sortedMapping[entry[0]] = entry[1];
      });
    return sortedMapping;
  }

  // @ts-ignore: Engine is imported as any
  const [dlmRulesEngine, setDlmRulesEngine] = useState<any>(new (require('json-rules-engine').Engine)());
  const classes: any = useStyles();
  const tableCellClasses = classnames(classes.tableCell);
  const [dynamicLenderMapping, setDynamicLenderMapping] = useState<any>(null);
  const [structuredData, setStructuredData] = useState<any>(null);
  const [loading, setLoading] = useState<any>(true);
  const [runningRules, setRunningRules] = useState<any>(true);
  const [requiredFieldsStillEmpty, setRequiredFieldsStillEmpty] = useState<string[]>([]);
  const [submittingApp, setSubmittingApp] = useState<any>(false);
  const [openDlmErrorSnackbar, setOpenDlmErrorSnackbar] = useState<any>(false);
  const [dlmErrorSnackbarMessage, setDlmErrorSnackbarMessage] = useState<any>('');
  const [dlmInputSetValueFunctions, setDlmInputSetValueFunctions] = useState<any>({});
  const [dlmInputSetRequiredFunctions, setDlmInputSetRequiredFunctions] = useState<any>({});
  const [dlmInputSetErrorFunctions, setDlmInputSetErrorFunctions] = useState<any>({});
  const [dlmInputSetOptionSetFunctions, setDlmInputSetOptionSetFunctions] = useState<any>({});
  const [submitDisabled, setSubmitDisabled] = useState<any>(false);
  const collapseInitialState: any = {};
  const [collapseState, setCollapseState] = useState<any>(collapseInitialState);
  const [currentTabSelected, setCurrentTabSelected] = useState<any>('dlm');
  const [showMissingFields, setShowMissingFields] = useState<boolean>(false);

  const toggleCollapse = (sectionId: string) =>
    setCollapseState({ ...collapseState, [sectionId]: !collapseState[sectionId] });
  const handleTabChange = (event: React.ChangeEvent<{}>, newValue: string) => {
    setCurrentTabSelected(newValue);
  };
  const formatDCRProperty = (property: string): string[] => {
    if (!property) return [''];
    let strProps = property.split('|').map((prop: string) =>
      prop
        .split('.')
        .filter((prop: string) => prop !== 'INTETIONALLY_BAD_MAPPING' && prop !== 'BAD_INPUT')
        .map((prop: string) => {
          let newProp: string = '';
          newProp = prop.replace('[0]', '');
          newProp = prop.replace('[1]', '');
          newProp = prop.replace('[2]', '');
          if (newProp === 'creditApp') newProp = 'Credit Application';
          else if (newProp.includes('creditApplicationOwner')) newProp = 'Owner';
          else if (newProp.includes('reference')) {
            try {
              const referenceJson = JSON.parse(property.match(/reference\[(.*?)\]/)?.[1] || '{}');
              newProp = `Reference (${referenceJson.type})`;
            } catch (e) {
              newProp = 'Reference';
            }
          } else if (newProp.includes('equipments')) newProp = 'Equipment';
          else if (newProp === 'vendorOpportunity') newProp = 'Opportunity';
          else if (newProp === 'userProfile') newProp = 'Current User';
          else if (newProp === 'null') newProp = '';
          else
            newProp = newProp.replace(/([A-Z])/g, ' $1').replace(/^./, function (str: string) {
              return str.toUpperCase();
            });
          if (newProp === 'Ssn') {
            return 'SSN';
          } else if (newProp.includes('Fedaral')) {
            newProp = newProp.replace('Fedaral', 'Federal');
          }
          return newProp;
        })
    );
    return strProps.map((prop: string[]) => prop.join(': '));
  };
  const getTrnsactDisplayName = (field: any): string => {
    let displayName: string | undefined = undefined;
    try {
      const keyToFind: string = field.details.dcrPropertySource
        .replace('creditApp.', '')
        .replace('[', '.')
        .replace(']', '');
      portalConfiguration.jsonDefinition.sections.forEach((section: any) => {
        section.blocks.forEach((block: any) => {
          const matchingField = block.fields.find((field: any) => {
            return field.key === keyToFind || field.ambiguousKey === keyToFind;
          });
          if (matchingField) {
            displayName = matchingField.ocaConfig.config.displayLabel.en;
          }
        });
      });
      if (!displayName) {
        displayName = field.details.displayName;
      }
      return displayName || '';
    } catch (e) {
      return field.details.displayName || '';
    }
  };
  const getTrnsactDisplayValue = (field: any): string => {
    if (field.details.dcrPropertySource.includes('legalStructure')) {
      const selectedLso = (legalStructureOption as any).find((lso: any) => lso.value === field.originalMappedValue);
      if (selectedLso) return selectedLso.label;
    }
    if (field.originalMappedValue === true) return 'Yes';
    if (field.originalMappedValue === false) return 'No';
    return field.originalMappedValue ? field.originalMappedValue : ' ';
  };

  const handleDynamicLenderMappingValueChange = useCallback(
    (newValue: any, lenderMappingId: any) => {
      if (!dynamicLenderMapping) return;
      const index = dynamicLenderMapping.findIndex((el: any) => el.lenderProperty === lenderMappingId);
      if (index === -1) return;
      // Mutate then clone to preserve existing object references expected by child components
      dynamicLenderMapping[index].mappedValue = newValue;
      dlmRulesEngine.addFact(lenderMappingId, newValue);
      setDynamicLenderMapping([...dynamicLenderMapping]);
      // Debounce rules engine runs to avoid thrash during rapid typing
      if ((window as any).__dlmRulesRunTimeout) {
        clearTimeout((window as any).__dlmRulesRunTimeout);
      }
      (window as any).__dlmRulesRunTimeout = setTimeout(() => {
        dlmRulesEngine.run().then(({ events }: { events: Array<{ type: string; params: any }> }) => {
          handleDlmRulesEvents(events);
        });
      }, 150); // 150ms debounce
    },
    [dynamicLenderMapping, dlmRulesEngine]
  );

  const getInputProps = (lenderField: any) => {
    if (lenderField?.details?.rulesConfiguration?.configuration) {
      const inputProps = lenderField?.details?.rulesConfiguration?.configuration;

      return inputProps;
    }
    return {};
  };
  const setReferenceToChildSetValueFunction = (lenderProperty: any, setValueFunction: any) => {
    dlmInputSetValueFunctions[lenderProperty] = setValueFunction;
    setDlmInputSetValueFunctions(dlmInputSetValueFunctions);
  };

  const setReferenceToChildSetRequiredFunction = (lenderProperty: any, setDisplayFunction: any) => {
    dlmInputSetRequiredFunctions[lenderProperty] = setDisplayFunction;
    setDlmInputSetRequiredFunctions(dlmInputSetRequiredFunctions);
  };

  const setReferenceToChildSetErrorsFunction = (lenderProperty: any, setErrorFunction: any) => {
    dlmInputSetErrorFunctions[lenderProperty] = setErrorFunction;
    setDlmInputSetErrorFunctions(dlmInputSetErrorFunctions);
  };

  const setReferenceToSetOptionSetsFunction = (lenderProperty: any, setOptionSetFunction: any) => {
    dlmInputSetOptionSetFunctions[lenderProperty] = setOptionSetFunction;
    setDlmInputSetOptionSetFunctions(dlmInputSetOptionSetFunctions);
  };
  const isDate = function (date: any) {
    return new Date(date).toString() !== 'Invalid Date' && !isNaN(new Date(date).getTime());
  };
  const createNewField = (type: any, field: any) => {
    switch (type) {
      case 'YEAR':
        let yearsArray = getYearsArray().map(yr => ({ displayName: yr.toString(), lenderValue: yr.toString() }));
        field.details.optionSets = yearsArray;
        if (field.mappedValue) {
          if (typeof field.mappedValue === 'string' && isDate(field.mappedValue)) {
            try {
              field.mappedValue = String(moment(field.mappedValue).year());
            } catch (error) {
              console.log('Error with mappedValue with field type Year');
            }
          }
        }
        return field;
      case 'MONTH':
        let monthsArray = getMonthsArray().map(month => ({ displayName: month.long, lenderValue: month.long }));
        field.details.optionSets = monthsArray;
        if (field.mappedValue && typeof field.mappedValue === 'string') {
          try {
            if (!isNaN(Date.parse(field.mappedValue))) field.mappedValue = moment(field.mappedValue).format('MMMM');
          } catch (error) {
            console.log('Error with mappedValue with field type Month');
          }
        }
        return field;
      case 'DAY':
        let daysArray = getDaysArray().map(day => ({ displayName: day.toString(), lenderValue: day.toString() }));
        field.details.optionSets = daysArray;
        if (field.mappedValue && typeof field.mappedValue === 'string' && field.mappedValue.length > 2) {
          try {
            field.mappedValue = String(moment(field.mappedValue).date());
          } catch (error) {
            console.log('Error with mappedValue with field type Day');
          }
        }
        return field;
      case 'STATE':
        const stateIndex = field.mappedValue?.length <= 2 ? 'abbr' : 'name';
        let statesArray = getStatesArray().map(state => ({
          displayName: state[stateIndex],
          lenderValue: state[stateIndex],
        }));
        field.details.optionSets = statesArray;

        return field;
      case 'STATE_ABBR':
        let statesABBRArray = getStatesArray().map(state => ({
          displayName: state['abbr'],
          lenderValue: state['abbr'],
        }));
        field.details.optionSets = statesABBRArray;

        return field;
      case 'COUNTRY':
        const countryIndex = field.mappedValue?.length <= 2 ? 'code' : 'name';
        let countriesArray = getCountriesArray().map(country => ({
          displayName: country[countryIndex],
          lenderValue: country[countryIndex],
        }));
        field.details.optionSets = countriesArray;

        return field;
      case 'COUNTRY_CODE':
        let countryCodeArray = getCountriesArray().map(country => ({
          displayName: `${country['name']} (${country['code']})`,
          lenderValue: country['code'],
        }));
        field.details.optionSets = countryCodeArray;

        return field;
    }
  };
  const renderField = useCallback(
    (field: any, id: any) => {
      const type = field.details?.fieldInputType;
      let newField = { ...field };
      switch (type) {
        case 'DATE':
          return (
            <DateFieldComp
              key={id}
              field={field}
              type={type}
              index={id}
              handleBlur={handleDynamicLenderMappingValueChange}
              setReference={setReferenceToChildSetValueFunction}
              setReferenceToRequiredFunction={setReferenceToChildSetRequiredFunction}
              setReferenceToErrorsValueFunction={setReferenceToChildSetErrorsFunction}
              setReferenceToSetOptionSetsFunction={setReferenceToSetOptionSetsFunction}
              inputProps={getInputProps(field)}
            />
          );
        case 'YEAR':
        case 'MONTH':
        case 'DAY':
        case 'STATE':
        case 'STATE_ABBR':
        case 'COUNTRY_CODE':
        case 'COUNTRY':
          newField = createNewField(type, newField);
          if (field.mappedValue !== newField.mappedValue)
            handleDynamicLenderMappingValueChange(newField.mappedValue, id);
          return (
            <SelectFieldComp
              field={newField}
              type={type}
              index={id}
              handleBlur={handleDynamicLenderMappingValueChange}
              key={id}
              setReference={setReferenceToChildSetValueFunction}
              setReferenceToRequiredFunction={setReferenceToChildSetRequiredFunction}
              setReferenceToErrorsValueFunction={setReferenceToChildSetErrorsFunction}
              setReferenceToSetOptionSetsFunction={setReferenceToSetOptionSetsFunction}
              inputProps={getInputProps(field)}
            />
          );
        case 'SSN':
          if (field.mappedValue !== newField.mappedValue)
            handleDynamicLenderMappingValueChange(newField.mappedValue, id);
          return (
            <TextFieldComp
              field={newField}
              type={type}
              index={id}
              handleBlur={handleDynamicLenderMappingValueChange}
              key={id}
              setReference={setReferenceToChildSetValueFunction}
              setReferenceToRequiredFunction={setReferenceToChildSetRequiredFunction}
              setReferenceToErrorsValueFunction={setReferenceToChildSetErrorsFunction}
              inputProps={getInputProps(field)}
              mask="999-99-9999"
            />
          );
        case 'PHONE_NUMBER':
          if (field.mappedValue !== newField.mappedValue)
            handleDynamicLenderMappingValueChange(newField.mappedValue, id);
          return (
            <TextFieldComp
              field={newField}
              type={type}
              index={id}
              handleBlur={handleDynamicLenderMappingValueChange}
              key={id}
              setReference={setReferenceToChildSetValueFunction}
              setReferenceToRequiredFunction={setReferenceToChildSetRequiredFunction}
              setReferenceToErrorsValueFunction={setReferenceToChildSetErrorsFunction}
              inputProps={getInputProps(field)}
              mask="(999) 999-9999"
            />
          );
        case 'OPTION_SET':
          return (
            <SelectFieldComp
              field={field}
              type={type}
              index={id}
              handleBlur={handleDynamicLenderMappingValueChange}
              key={id}
              setReference={setReferenceToChildSetValueFunction}
              setReferenceToRequiredFunction={setReferenceToChildSetRequiredFunction}
              setReferenceToErrorsValueFunction={setReferenceToChildSetErrorsFunction}
              setReferenceToSetOptionSetsFunction={setReferenceToSetOptionSetsFunction}
              inputProps={{}}
            />
          );
        default:
          return (
            <TextFieldComp
              field={field}
              type={type}
              index={id}
              handleBlur={handleDynamicLenderMappingValueChange}
              key={id}
              setReference={setReferenceToChildSetValueFunction}
              setReferenceToRequiredFunction={setReferenceToChildSetRequiredFunction}
              setReferenceToErrorsValueFunction={setReferenceToChildSetErrorsFunction}
              inputProps={getInputProps(field)}
              validRegexPattern={field.details?.validRegexPattern}
            />
          );
      }
    },
    [handleDynamicLenderMappingValueChange]
  );

  // Memoize derived list of category entries from structuredData
  const categoryEntries = useMemo(() => (structuredData ? Object.entries(structuredData) : []), [structuredData]);
  const checkAllRequiredFieldsHaveValues = () => {
    let allRequiredFieldsHaveValue = true;
    let requiredFieldsEmpty: string[] = [];
    dynamicLenderMapping.forEach((field: any) => {
      if (
        (field.details.fieldInputType === 'OPTION_SET'
          ? !field.details?.optionSets?.find((os: any) => os.lenderValue === field.mappedValue)
          : !field.mappedValue) &&
        field.mappedValue !== 0 &&
        field.details.required === 'REQUIRED' &&
        !field.details.hidden
      ) {
        requiredFieldsEmpty.push(field.details.displayName);
        allRequiredFieldsHaveValue = false;
      }
    });
    setRequiredFieldsStillEmpty(requiredFieldsEmpty);
    return allRequiredFieldsHaveValue;
  };
  const handleLMSFormSubmit = () => {
    const getFieldsWithErrors = () => {
      const fieldsWithErrors: any[] = [];
      if (!dynamicLenderMapping) return fieldsWithErrors;
      dynamicLenderMapping.forEach((field: any) => {
        try {
          if (field.details.errors && field.details.errors.length > 0 && field.details.hidden === false) {
            fieldsWithErrors.push(field);
          }
        } catch (e) {}
      });
      return fieldsWithErrors;
    };
    const removeMasks = () => {
      if (!dynamicLenderMapping) return;
      dynamicLenderMapping.forEach((field: any, i: number) => {
        try {
          if (dynamicLenderMapping[i].mappedValue) {
            if (field.details?.fieldInputType === 'PHONE_NUMBER') {
              dynamicLenderMapping[i].mappedValue = dynamicLenderMapping[i].mappedValue.replace(/[()\-\s]/g, '');
            } else if (field.details?.fieldInputType === 'SSN' || field.details?.fieldInputType === 'FEIN') {
              dynamicLenderMapping[i].mappedValue = dynamicLenderMapping[i].mappedValue.replace(/-/g, '');
            }
          }
        } catch (e) {}
      });
    };
    const fieldsWithErrors = getFieldsWithErrors();
    const allRequiredFieldsHaveValue = checkAllRequiredFieldsHaveValues();
    if (allRequiredFieldsHaveValue && fieldsWithErrors.length === 0) {
      removeMasks();
      setSubmittingApp(true);
      submitCallback(dynamicLenderMapping);
      handleClose();
      setOpenDlmErrorSnackbar(false);
      setDlmErrorSnackbarMessage('');
    }
  };

  const {
    data: dynamicLenderMappingData,
    loading: dynamicLenderMappingDataLoading,
    called: getDynamicLenderMappingCalled,
  } = useQuery(GET_DYNAMIC_LENDER_MAPPING, {
    variables: {
      lenderType: lmsCodeName,
      vendorOpportunityId: vo.vendorOpportunityId,
      lenderProfileDynamicsId: lender.id,
    },
    fetchPolicy: 'no-cache',
  });

  const setCollapseStateFromDlmErrors = () => {
    if (!dynamicLenderMapping) {
      return;
    }
    const localCollapseState = { ...collapseState };
    dynamicLenderMapping.forEach((field: any) => {
      const categoryCollapseId = `category-${field.details.category}`;
      const sectionCollapseId = `section-${field.details.section}`;

      let optionSetValueIsOkay = true;
      try {
        if (field.details.fieldInputType === 'OPTION_SET') {
          const validOptionSet = field.details.optionSets.find((os: any) => os.lenderValue === field.mappedValue);
          if (!validOptionSet) {
            optionSetValueIsOkay = false;
          }
        }
      } catch (e: any) {
        console.log(`Error checking if we should collapse a section with option sets: ${e.message}`);
        optionSetValueIsOkay = false;
      }

      if (
        (!field.mappedValue &&
          field.mappedValue !== 0 &&
          field.details.required === 'REQUIRED' &&
          !field.details.hidden) ||
        !optionSetValueIsOkay
      ) {
        localCollapseState[categoryCollapseId] = false;
        localCollapseState[sectionCollapseId] = false;
      } else {
        if (localCollapseState[categoryCollapseId] !== false) {
          localCollapseState[categoryCollapseId] = true;
        }
        if (localCollapseState[sectionCollapseId] !== false) {
          // console.log(`_ _ _ setCollapseStateFromDlmErrors: setting ${categoryCollapseId} | ${sectionCollapseId} to TRUE: ${field.lenderProperty} = ${field.mappedValued} | REQUIRED: ${field.details.required}`);
          localCollapseState[sectionCollapseId] = true;
        }
      }
    });
    setCollapseState(localCollapseState);
  };

  const handleDlmRulesEvents = (events: any) => {
    events.forEach((event: any) => {
      // console.log(`EVENT TRIGGERED: ${JSON.stringify(event)}`);
      if (event.params.actions) {
        event.params.actions.forEach(async (action: any) => {
          // console.log(`setInputValue: ${action.inputKey} action: ${JSON.stringify(action)}`);
          if (action.type === 'setInputValue') {
            if (_.has(action, 'value') && action.inputKey && dlmInputSetValueFunctions[action.inputKey]) {
              dlmInputSetValueFunctions[action.inputKey](action.value);
            } else if (action.factKey && action.inputKey && dlmInputSetValueFunctions[action.inputKey]) {
              const fact = dlmRulesEngine.getFact(action.factKey);
              dlmInputSetValueFunctions[action.inputKey](fact.value);
            } else if (
              action.operations &&
              _.isArray(action.operations) &&
              dlmInputSetValueFunctions[action.inputKey]
            ) {
              try {
                let finalValue: any;
                action.operations.forEach((o: any) => {
                  const operation = o.operation;
                  const outputType = o.outputType;
                  if (operation && o.operands && _.isArray(o.operands)) {
                    o.operands.forEach((operand: any) => {
                      const operandTyped = operand;
                      let operandValue;
                      if (_.isObject(operandTyped) && (operandTyped as any).factKey) {
                        const fact = dlmRulesEngine.getFact((operandTyped as any).factKey);
                        if (_.isString(fact.value)) {
                          operandValue = outputType === 'INTEGER' ? parseInt(fact.value) : parseFloat(fact.value);
                        } else {
                          operandValue = fact.value;
                        }
                      } else {
                        operandValue = operand;
                      }

                      if (!finalValue && _.isNumber(operandValue)) {
                        finalValue = operandValue;
                      } else if (operandValue) {
                        if (operation === 'DIFFERENCE') {
                          finalValue = finalValue - operandValue;
                        } else if (operation === 'SUM') {
                          finalValue = finalValue + operandValue;
                        }
                      }
                    });
                  } else {
                    console.warn(
                      `setInputValue: triggered with operations but no value could be found: ${JSON.stringify(action)}`
                    );
                  }
                });
                dlmInputSetValueFunctions[action.inputKey](finalValue);
              } catch (e: any) {
                console.error(
                  `setInputValue triggered with operations but an error occurred: ${JSON.stringify(action)}: ${
                    e.message
                  }`
                );
              }
            } else {
              console.warn(
                `setInputValue: ${action.inputKey}: triggered but no value was found: ${JSON.stringify(action)}`
              );
            }
          } else if (action.type === 'setInputRequired') {
            if (dlmInputSetRequiredFunctions[action.inputKey]) {
              try {
                if (dynamicLenderMapping) {
                  dlmInputSetRequiredFunctions[action.inputKey](action.value);
                  const dlmInputIndex = dynamicLenderMapping.findIndex(
                    (el: any) => el.lenderProperty === action.inputKey
                  );
                  dynamicLenderMapping[dlmInputIndex].details.required = action.value ? 'REQUIRED' : 'OPTIONAL';
                }
              } catch (e: any) {
                console.log(`Error setting dlmInputSetRequiredFunctions[${action.inputKey}]: ${e.message}`);
              }
            }
          } else if (action.type === 'setInputHidden') {
            try {
              let dlmInputIndex = dynamicLenderMapping.findIndex((el: any) => el.lenderProperty === action.inputKey);
              if (dlmInputIndex !== -1) {
                dynamicLenderMapping[dlmInputIndex].details.hidden = action.value;
                dlmRulesEngine.addFact(`${action.inputKey}_hidden`, action.value);
              } else {
                let inputIteratorToHide = 0;
                do {
                  dlmInputIndex = dynamicLenderMapping.findIndex(
                    (el: any) => el.lenderProperty === `${action.inputKey}[${inputIteratorToHide}]`
                  );
                  dynamicLenderMapping[dlmInputIndex].details.hidden = action.value;
                  dlmRulesEngine.addFact(`${action.inputKey}_hidden`, action.value);
                  inputIteratorToHide++;
                } while (dlmInputIndex !== -1);
              }
            } catch (e: any) {
              console.log(`Error setting dlmInputSetHiddenFunctions[${action.inputKey}]: ${e.message}`);
            }
          } else if (action.type === 'setInputError') {
            try {
              const dlmInputIndex = dynamicLenderMapping.findIndex((el: any) => el.lenderProperty === action.inputKey);
              let currentErrors = _.get(dynamicLenderMapping[dlmInputIndex], 'details.errors', []);
              /*
                errors MUST have the following shape:
                {
                  name: string, // the KEY of the error
                  message: string, // the human readable error message
                }
                */
              if (!currentErrors.find((error: any) => error.name === action.error.name)) {
                currentErrors.push(action.error);
                dynamicLenderMapping[dlmInputIndex].details.errors = currentErrors;
              }
            } catch (e: any) {
              console.log(`Error setting dlmInputSetErrorFunctions[${action.inputKey}]: ${e.message}`);
            }
          } else if (action.type === 'removeInputError') {
            try {
              const dlmInputIndex = dynamicLenderMapping.findIndex((el: any) => el.lenderProperty === action.inputKey);
              let currentErrors = _.get(dynamicLenderMapping[dlmInputIndex], 'details.errors', []);
              dynamicLenderMapping[dlmInputIndex].details.errors = currentErrors.filter(
                (error: any) => !action.errorsToClear.includes(error.name)
              );
              dlmInputSetErrorFunctions[action.inputKey](dynamicLenderMapping[dlmInputIndex].details.errors);
            } catch (e: any) {
              console.log(`Error setting dlmInputSetErrorFunctions[${action.inputKey}]: ${e.message}`);
            }
          } else if (action.type === 'removeRule') {
            console.log(`Removing rule: ${action.ruleName}`);
            dlmRulesEngine.removeRule(action.ruleName);
          } else if (action.type === 'setOptionSetInputValue') {
            try {
              const optionSetsFact = dlmRulesEngine.getFact(`${action.inputKey}-optionSets`);
              const likeDisplay = action.likeDisplay;
              const optionSet = optionSetsFact.value.find((os: any) => {
                return os.displayName.toLowerCase().includes(likeDisplay.toLowerCase());
              });
              console.log(`Option Set Found via likeDisplay ${likeDisplay}: ${JSON.stringify(optionSet)}`);
              if (dlmInputSetValueFunctions[action.inputKey])
                dlmInputSetValueFunctions[action.inputKey](optionSet.lenderValue);
            } catch (e: any) {
              console.log(`Error setting default option set for ${action.inputKey}: ${e.message}`);
            }
          } else if (action.type === 'getCascadingOptionSets') {
            console.log(
              `getCascadingOptionSets   FACT: ${JSON.stringify(dlmRulesEngine.getFact(action.parentInputKey))}`
            );
            console.log(
              `getCascadingOptioNSets PARENT OPTION SETS: ${JSON.stringify(
                dlmRulesEngine.getFact(`${action.parentInputKey}-optionSets`)
              )}`
            );
            const parentSelectedValue = dlmRulesEngine.getFact(action.parentInputKey);
            console.log(`getCascadingOptionSets: parentSelectedValue: ${JSON.stringify(parentSelectedValue)}`);

            const parentOptionSets = dlmRulesEngine.getFact(`${action.parentInputKey}-optionSets`);

            if (parentOptionSets) {
              const selectedParentOptionSet = parentOptionSets.value.find((os: any) => {
                return os.lenderValue === parentSelectedValue.value;
              });
              console.log(
                `getCascadingOptionSets: selectedParentOptionSet: ${JSON.stringify(selectedParentOptionSet)}`
              );
            }
          }
        });
      }
    });
    setCollapseStateFromDlmErrors();
  };
  useEffect(() => {
    if (!dynamicLenderMappingDataLoading && getDynamicLenderMappingCalled) {
      try {
        dynamicLenderMappingData.getMappedLenderValuesForVendorOpportunity.forEach((dv: any) => {
          if (dv.details?.fieldInputType === 'OPTION_SET' && Array.isArray(dv.details.optionSets)) {
            dv.details.optionSets = _.uniqBy(dv.details.optionSets, 'displayName');
          }
          try {
            const rules = dv.details?.rulesConfiguration?.rules;

            if (rules) {
              rules.forEach((rule: any) => {
                // console.log(`Adding rule: ${JSON.stringify(rule)}`);
                const conditionsAll = rule.conditions.all ? rule.conditions.all : [];
                const conditionsAny = rule.conditions.any ? rule.conditions.any : [];
                const conditionsNot = rule.conditions.not ? rule.conditions.not : [];
                const conditions = conditionsAll.concat(conditionsAny).concat(conditionsNot);

                conditions.forEach((c: any) => {
                  if (c.fact && !dlmRulesEngine.getFact(c.fact)) {
                    dlmRulesEngine.addFact(c.fact, null);
                  }
                  if (c?.value?.fact && !dlmRulesEngine.getFact(c.value.fact)) {
                    dlmRulesEngine.addFact(c.value.fact, null);
                  }
                });
                dlmRulesEngine.addRule(rule);
              });
            }
          } catch (e: any) {
            console.log(`Error adding DLM Rule: ${e.message}`);
          }
        });
      } catch (e: any) {
        // Error finding transactionSummary
        console.log(e.message);
      }

      let structuredMapping: any = structureFields(
        dynamicLenderMappingData.getMappedLenderValuesForVendorOpportunity,
        'category'
      );
      Object.entries(structuredMapping).forEach(
        (entry: any) => (structuredMapping[entry[0]] = structureFields(entry[1], 'section'))
      );
      structuredMapping = sortByOrdinal(structuredMapping);
      setStructuredData(structuredMapping);

      setDynamicLenderMapping(dynamicLenderMappingData.getMappedLenderValuesForVendorOpportunity);

      setLoading(false);

      try {
        const localCollapseState: any = {};

        dynamicLenderMappingData.getMappedLenderValuesForVendorOpportunity.forEach((field: any) => {
          const categoryCollapseId = `category-${field.details.category}`;
          const sectionCollapseId = `section-${field.details.section}`;
          localCollapseState[categoryCollapseId] = true;
          localCollapseState[sectionCollapseId] = true;
        });

        setCollapseState(localCollapseState);
      } catch (e) {}

      setDlmRulesEngine(dlmRulesEngine);

      dlmRulesEngine.run().then(({ events }: { events: Array<{ type: string; params: any }> }) => {
        handleDlmRulesEvents(events);
      });
    }
  }, [dynamicLenderMappingDataLoading, getDynamicLenderMappingCalled]);

  useEffect(() => {
    if (dynamicLenderMapping) {
      setSubmitDisabled(!checkAllRequiredFieldsHaveValues());
    }
  }, [dynamicLenderMapping]);

  useEffect(() => {
    if (!loading) {
      setTimeout(() => {
        dlmRulesEngine.run().then(({ events }: { events: Array<{ type: string; params: any }> }) => {
          handleDlmRulesEvents(events);
          setRunningRules(false);
        });
      }, 150);
    }
  }, [loading]);
  if (loading)
    return (
      <div className={classes.modalStyle}>
        <CircularProgress className="progressLoader" size={48} />
        <div className={classes.headerContainer}>
          <h4>Loading Submission Data...</h4>
        </div>
      </div>
    );

  return (
    <div className={classes.modalStyle}>
      <Tabs
        value={currentTabSelected}
        onChange={handleTabChange}
        variant="fullWidth"
        aria-label="full width tabs example"
        TabIndicatorProps={{
          style: {
            backgroundColor: (theme as any).palette.primary.main,
          },
        }}
      >
        <Tab
          value="dlm"
          label={`${lmsName} Data Sheet`}
          style={{ color: currentTabSelected === 'dlm' ? (theme as any).palette.primary.main : '' }}
        />
      </Tabs>

      <TabPanel value={currentTabSelected} index="dlm">
        <div style={{ paddingBottom: 16 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <Alert
              severity="warning"
              icon={false}
              style={{
                fontWeight: 500,
                fontSize: 15,
                background: 'rgba(255, 193, 7, 0.12)',
                color: '#b26a00',
                padding: '6px 14px',
                borderRadius: 6,
              }}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span role="img" aria-label="warning" style={{ fontSize: 20, color: '#b26a00' }}>
                  ⚠️
                </span>
                All fields highlighted in <span style={{ color: '#f44336', fontWeight: 700 }}>red</span> must be
                provided
              </span>
            </Alert>

            {requiredFieldsStillEmpty.length > 0 && (
              <Alert
                severity="error"
                style={{
                  fontSize: 14,
                  background: 'rgba(244, 67, 54, 0.12)',
                  color: '#c62828',
                  padding: '12px 16px',
                  borderRadius: 6,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    cursor: 'pointer',
                    userSelect: 'none',
                  }}
                  onClick={() => setShowMissingFields(!showMissingFields)}
                >
                  <div style={{ fontWeight: 600 }}>Missing Required Fields ({requiredFieldsStillEmpty.length})</div>
                  <IconButton
                    size="small"
                    style={{ color: '#c62828', padding: 4 }}
                    onClick={e => {
                      e.stopPropagation();
                      setShowMissingFields(!showMissingFields);
                    }}
                  >
                    {showMissingFields ? (
                      <span style={{ fontSize: 18 }}>▼</span>
                    ) : (
                      <span style={{ fontSize: 18 }}>▶</span>
                    )}
                  </IconButton>
                </div>
                {showMissingFields && (
                  <div
                    style={{
                      maxHeight: '200px',
                      overflowY: 'auto',
                      marginTop: 8,
                      width: '100%',
                      paddingRight: 8,
                    }}
                  >
                    <ul style={{ margin: 0, paddingLeft: 20, lineHeight: 1.8 }}>
                      {requiredFieldsStillEmpty.map((fieldName, index) => (
                        <li key={index} style={{ marginBottom: 4 }}>
                          {fieldName}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </Alert>
            )}
          </div>
        </div>
        <Table className={classes.table} style={{ marginBottom: 8 }}>
          <TableHead className={classes.tableHeader}>
            <TableCell
              style={{ fontSize: 16, fontWeight: 'bold', textAlign: 'center', marginRight: '8px' }}
              className={tableCellClasses}
              width="40%"
            >
              {[`Trnsact - ${lender.name}`]}
            </TableCell>
            <TableCell style={{ fontSize: 16, fontWeight: 'bold' }} className={tableCellClasses} width="20%">
              {['']}
            </TableCell>
            <TableCell
              style={{ fontSize: 16, fontWeight: 'bold', marginRight: '8px' }}
              className={tableCellClasses}
              width="40%"
            >
              {[lmsName]}
            </TableCell>
          </TableHead>
        </Table>
        <div style={{ maxHeight: '50vh', overflowY: 'auto' }}>
          {dynamicLenderMapping &&
            categoryEntries.map((category: any) => (
              <div style={{ marginBottom: 40, width: '98%' }}>
                {category[0] !== 'DEFAULT' &&
                  Object.entries(category[1]).find((s: any) => {
                    return s[1].find((r: any) => {
                      return r.details.hidden !== true;
                    });
                  }) && (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <span style={{ fontWeight: 500 }}>{category[0]}</span>
                      <span
                        className={classes.collapseButton + ' collapseButton'}
                        onClick={() => toggleCollapse(`category-${category[0]}`)}
                      >
                        {collapseState[`category-${category[0]}`] ? (
                          <div className={classes.icon + ' ' + classes.iconPlus} />
                        ) : (
                          <div className={classes.icon + ' ' + classes.iconMinus} />
                        )}
                      </span>
                    </div>
                  )}
                {Object.entries(category[1]).map((section: any) => (
                  <>
                    {section[0] !== 'DEFAULT' &&
                      section[1].find((r: any) => {
                        return r.details.hidden !== true;
                      }) &&
                      !collapseState[`category-${category[0]}`] && (
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <span style={{ textDecoration: 'italic', margin: '16px 0' }}>{section[0]}</span>
                          <span
                            className={classes.collapseButton + ' collapseButton'}
                            onClick={() => toggleCollapse(`section-${section[0]}`)}
                          >
                            {collapseState[`section-${section[0]}`] ? (
                              <div className={classes.icon + ' ' + classes.iconPlus} />
                            ) : (
                              <div className={classes.icon + ' ' + classes.iconMinus} />
                            )}
                          </span>
                        </div>
                      )}

                    {section[1].map((row: any) => (
                      <Table
                        className={classes.table}
                        style={{
                          display:
                            !collapseState[`category-${category[0]}`] && !collapseState[`section-${section[0]}`]
                              ? 'table'
                              : 'none',
                        }}
                      >
                        <TableBody>
                          {row.details.hidden !== true && (
                            <TableRow>
                              <TableCell className={tableCellClasses} width="40%">
                                <Box component="form" noValidate autoComplete="off">
                                  {row.details.dcrPropertySource && row.details.dcrPropertySource !== '' && (
                                    <>
                                      <TextField
                                        className={classes.textField}
                                        disabled={true}
                                        fullWidth={true}
                                        label={
                                          (row.details.required === 'REQUIRED' ? '* ' : '') +
                                            formatDCRProperty(row.details.dcrPropertySource)[0] ||
                                          getTrnsactDisplayName(row)
                                        }
                                        defaultValue={getTrnsactDisplayValue(row)}
                                        variant="outlined"
                                      />
                                      {formatDCRProperty(row.details.dcrPropertySource).length > 1 && (
                                        <p
                                          style={{
                                            fontSize: 12,
                                            fontWeight: 500,
                                            color: 'rgba(0, 0, 0, 0.38)',
                                            marginBottom: 8,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                          }}
                                        >
                                          Alt Source:{' '}
                                          {formatDCRProperty(row.details.dcrPropertySource)
                                            .map((src: any, i: any) => i !== 0 && src)
                                            .filter((i: any) => i)
                                            .join(' OR ')}
                                        </p>
                                      )}
                                    </>
                                  )}
                                  {(!row.details.dcrPropertySource || row.details.dcrPropertySource === '') && (
                                    <span className={classes.labelNotMapped}>No field target</span>
                                  )}
                                </Box>
                              </TableCell>
                              <TableCell
                                className={tableCellClasses}
                                width="10%"
                                padding-left="70px"
                                padding-top="24px"
                                align="center"
                              >
                                <img style={{ marginBottom: 16 }} src={twoWayArrow} width="20px" height="10px" />
                              </TableCell>
                              <TableCell
                                className={tableCellClasses}
                                width="40%"
                                style={{ display: 'flex', width: '100%', alignItems: 'center' }}
                              >
                                {renderField(row, row.lenderProperty)}
                                {!_.isEmpty(row.mappedValue) ? (
                                  <IconButton
                                    onClick={() => {
                                      handleDynamicLenderMappingValueChange(null, row.lenderProperty);
                                    }}
                                    style={{ height: 'fit-content' }}
                                    size="large"
                                  >
                                    <Clear fontSize="small" />
                                  </IconButton>
                                ) : (
                                  <div style={{ width: 48 }} />
                                )}
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    ))}
                  </>
                ))}
              </div>
            ))}
        </div>
      </TabPanel>
      <div style={{ display: 'flex', justifyContent: 'center', margin: '16px 0' }}>
        <Snackbar
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          open={openDlmErrorSnackbar}
          autoHideDuration={6000}
          onClose={() => {
            setOpenDlmErrorSnackbar(false);
          }}
        >
          <Alert severity={'error'}>{dlmErrorSnackbarMessage}</Alert>
        </Snackbar>
        <Button
          color="primary"
          autoFocus
          onClick={() => {
            if (checkAllRequiredFieldsHaveValues()) {
              handleLMSFormSubmit();
            }
          }}
          disabled={submitDisabled}
        >
          Book Deal
        </Button>
      </div>
    </div>
  );
};

export default LMSMappingModal;
